/**
 * Commons license templates offered as suggestions under "Enter a different
 * license".
 *
 * Every name here was checked against the live Commons API with
 * action=query&redirects=1, so each one resolves to a template that exists
 * and is spelled the way Commons spells it. Redirects were followed to their
 * targets, which is why this list says "PD-Art" and not "PD-art", and
 * "PD-USGov-HHS-NIH" and not "PD-USGov-NIH".
 *
 * `hint` is a plain-language gloss shown under the name. It is intentionally
 * not translated: the template names are not translated either, and a
 * half-translated list reads worse than an untranslated one.
 */
export interface LicenseTemplate {
  name: string
  hint: string
}

export const LICENSE_TEMPLATES: readonly LicenseTemplate[] = Object.freeze([
  /* Creative Commons */
  { name: "Cc-zero", hint: "CC0 - public domain dedication" },
  { name: "Cc-by-4.0", hint: "Attribution 4.0" },
  { name: "Cc-by-sa-4.0", hint: "Attribution-ShareAlike 4.0" },
  { name: "Cc-by-3.0", hint: "Attribution 3.0" },
  { name: "Cc-by-sa-3.0", hint: "Attribution-ShareAlike 3.0" },
  { name: "Cc-by-2.5", hint: "Attribution 2.5" },
  { name: "Cc-by-sa-2.5", hint: "Attribution-ShareAlike 2.5" },
  { name: "Cc-by-2.0", hint: "Attribution 2.0" },
  { name: "Cc-by-sa-2.0", hint: "Attribution-ShareAlike 2.0" },
  { name: "Cc-by-2.0-de", hint: "Attribution 2.0 Germany" },
  { name: "Cc-by-sa-2.0-de", hint: "Attribution-ShareAlike 2.0 Germany" },
  { name: "Cc-by-sa-3.0-de", hint: "Attribution-ShareAlike 3.0 Germany" },
  { name: "Cc-by-sa-3.0-at", hint: "Attribution-ShareAlike 3.0 Austria" },
  { name: "Cc-by-sa-3.0-nl", hint: "Attribution-ShareAlike 3.0 Netherlands" },
  { name: "Cc-by-sa-3.0-pl", hint: "Attribution-ShareAlike 3.0 Poland" },

  /* Other free licenses */
  { name: "GFDL", hint: "GNU Free Documentation License" },
  { name: "GFDL-self", hint: "GFDL, released by the uploader" },
  { name: "FAL", hint: "Free Art License" },
  { name: "Attribution only license", hint: "Free use with credit required" },
  { name: "Copyrighted free use", hint: "Copyright holder allows any use" },

  /* Public domain - by uploader or by age */
  { name: "PD-self", hint: "Released into the public domain by the uploader" },
  { name: "PD-author", hint: "Released into the public domain by its author" },
  { name: "PD-old-70", hint: "Author died more than 70 years ago" },
  { name: "PD-old-70-expired", hint: "Author died 70+ years ago, also PD in the US" },
  { name: "PD-old-100", hint: "Author died more than 100 years ago" },
  { name: "PD-old-100-expired", hint: "Author died 100+ years ago, also PD in the US" },
  { name: "PD-old-assumed", hint: "Published long ago, author unknown" },
  { name: "PD-Art", hint: "Photograph of a public-domain two-dimensional artwork" },

  /* Public domain - United States */
  { name: "PD-US", hint: "Public domain in the United States" },
  { name: "PD-US-expired", hint: "US copyright has expired" },
  { name: "PD-US-no notice", hint: "Published in the US without a copyright notice" },
  { name: "PD-US-not renewed", hint: "US copyright was not renewed" },
  { name: "PD-USGov", hint: "Work of the US federal government" },
  { name: "PD-USGov-NASA", hint: "Work of NASA" },
  { name: "PD-USGov-Military", hint: "Work of the US military" },
  { name: "PD-USGov-Military-Army", hint: "Work of the US Army" },
  { name: "PD-USGov-Military-Navy", hint: "Work of the US Navy" },
  { name: "PD-USGov-Military-Air Force", hint: "Work of the US Air Force" },
  { name: "PD-USGov-NOAA", hint: "Work of NOAA" },
  { name: "PD-USGov-USGS", hint: "Work of the US Geological Survey" },
  { name: "PD-USGov-DOE", hint: "Work of the US Department of Energy" },
  { name: "PD-USGov-HHS-NIH", hint: "Work of the National Institutes of Health" },
  { name: "PD-USGov-CIA", hint: "Work of the CIA" },
  { name: "PD-USGov-FBI", hint: "Work of the FBI" },
  { name: "PD-USGov-NPS", hint: "Work of the National Park Service" },
  { name: "PD-USGov-FWS", hint: "Work of the Fish and Wildlife Service" },
  { name: "PD-USGov-NIST", hint: "Work of NIST" },
  { name: "PD-USGov-Congress", hint: "Work of the US Congress" },
  { name: "PD-USGov-DOS", hint: "Work of the US Department of State" },

  /* Public domain - not copyrightable */
  { name: "PD-ineligible", hint: "Too simple to carry a copyright" },
  { name: "PD-text", hint: "Plain text, no original authorship" },
  { name: "PD-textlogo", hint: "Logo made only of text and simple shapes" },
  { name: "PD-shape", hint: "Simple geometric shapes" },
  { name: "PD-algorithm", hint: "Generated by an algorithm, including AI output" },

  /* Public domain and open licenses - by country */
  { name: "PD-India", hint: "Public domain in India" },
  { name: "GODL-India", hint: "Government Open Data License - India" },
  { name: "EdictGov-India", hint: "Indian government edicts and public records" },
  { name: "OGL3", hint: "UK Open Government Licence v3.0" },
  { name: "OGL-C", hint: "Canada Open Government Licence" },
  { name: "PD-UKGov", hint: "UK Crown copyright has expired" },
  { name: "PD-Canada", hint: "Public domain in Canada" },
  { name: "PD-AustrianGov", hint: "Austrian government work" },
  { name: "PD-GermanGov", hint: "German official work" },
  { name: "PD-ItalyGov", hint: "Italian government work" },
  { name: "PD-EU-no author disclosure", hint: "EU work published anonymously" },
  { name: "PD-China", hint: "Public domain in China" },
  { name: "PD-Japan-organization", hint: "Japanese corporate work, copyright expired" },
  { name: "KOGL Type 1", hint: "Korea Open Government License Type 1" },
  { name: "PD-RU-exempt", hint: "Russian official work, not copyrightable" },
  { name: "PD-South-Africa", hint: "Public domain in South Africa" },
  { name: "PD-Mexico", hint: "Public domain in Mexico" },
  { name: "PD-Iran", hint: "Public domain in Iran" },
  { name: "PD-Pakistan", hint: "Public domain in Pakistan" },
  { name: "PD-Nepal", hint: "Public domain in Nepal" },
  { name: "PD-Bangladesh", hint: "Public domain in Bangladesh" }
])

/**
 * Case- and separator-insensitive prefix-then-substring match, capped so the
 * popup never has to lay out 75 rows. Prefix hits come first because that is
 * what someone typing "pd-usg" is steering towards.
 *
 * The comparison key is precomputed once at module load, so a keystroke costs
 * one lowercase of the query plus one pass over a frozen array - no regex
 * construction and no allocation per candidate.
 */
const KEYS: readonly string[] = LICENSE_TEMPLATES.map(
  t => (t.name + " " + t.hint).toLowerCase().replace(/[\s_]+/g, "-"))

export function searchLicenseTemplates(query: string, limit = 8): LicenseTemplate[] {
  const q = query.trim().toLowerCase().replace(/[\s_]+/g, "-")
  if (!q) return LICENSE_TEMPLATES.slice(0, limit)
  const starts: LicenseTemplate[] = []
  const contains: LicenseTemplate[] = []
  for (let i = 0; i < KEYS.length; i++) {
    const at = KEYS[i].indexOf(q)
    if (at === 0) {
      if (starts.length < limit) starts.push(LICENSE_TEMPLATES[i])
    } else if (at > 0 && contains.length < limit) {
      contains.push(LICENSE_TEMPLATES[i])
    }
    if (starts.length >= limit) break
  }
  return starts.concat(contains).slice(0, limit)
}
